import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { staleGuard } from "src/utils/staleGuard";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  hasError: false,

  tags: [],
  tag: null,

  selectedTagProduct: null,
  // "Modo etiquetado" del camarero — cuando true, cada producto que
  // añade al cart toma `selectedTagProduct` como su `courseLabelID`.
  // Se resetea al cambiar de mesa (`resetTagMode`) para evitar marcar
  // por error productos de una mesa nueva con el curso de otra.
  tagModeActive: false,

  tagsPerPage: 10,
  selectedTagPage: 0,
  tagOrderBy: "asc",
  tagOrderProperty: "",
  tagsOrder: [],
  filterNameTags: "",
  seeAll: false,

  categories: [],
  category: null,
  createdSuccess: false,
  updatedSuccess: false,
  deletedSuccess: false,
  selectedCategoria: -1,
  order: [],
  imagesDb: [],
  images: [],
  selectedPage: 0,
  productPerPage: 10,
  productsOrder: [],
  categoriesOrder: [],
  filterName: "",
};

const slice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    setProductsOrder(state, action) {
      state.productsOrder = action.payload;
    },

    resetProductsOrder(state, action) {
      state.productsOrder = [];
    },

    setCategoriesOrder(state, action) {
      state.categoriesOrder = action.payload;
    },

    resetCategoriesOrder(state, action) {
      state.categoriesOrder = [];
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.hasError = true;
    },

    getImagesSuccess(state, action) {
      state.isLoading = false;
      state.imagesDb = action.payload;
    },

    // GET PRODUCTS
    getCategoriesSuccess(state, action) {
      state.isLoading = false;
      state.categories = action.payload;
    },

    // GET PRODUCT
    getCategorySuccess(state, action) {
      state.isLoading = false;
      state.category = action.payload;
      state.categories = [...state.categories, action.payload];
    },

    //
    updateCategorySuccess(state, action) {
      state.isLoading = false;
      state.categories = state.categories.map((category) => {
        if (Number(category.id) === Number(action.payload.id))
          return action.payload;
        else return category;
      });

      if (state.category !== null && state.category.id === action.payload.id) {
        state.category = action.payload;
      }

      state.updatedSuccess = true;
    },

    updateCategoryVisibilitySuccess(state, action) {
      state.isLoading = false;
      state.categories = state.categories.map((category) => {
        if (Number(category.id) === Number(action.payload.id))
          return {
            ...category,
            ...action.payload,
          };
        else return category;
      });
      state.updatedSuccess = true;
    },

    //
    deleteCategorySuccess(state, action) {
      state.isLoading = false;
      state.category = null;
      state.categories = state.categories.map((p) => {
        if (p.id === action.payload) {
          return { ...p, isActive: false, deletionDate: new Date() };
        } else {
          return p;
        }
      });
      state.deletedSuccess = true;
    },

    activateCategorySuccess(state, action) {
      state.isLoading = false;
      state.category = null;
      state.categories = state.categories.map((p) => {
        if (p.id === action.payload) {
          return { ...p, isActive: true, deletionDate: null };
        } else {
          return p;
        }
      });
      state.deletedSuccess = true;
    },

    //
    deleteSubCategorySuccess(state, action) {
      state.isLoading = false;
      state.category = null;
      state.categories = state.categories.filter((p) =>
        p.subCategories.filter((s) => s.id !== action.payload)
      );
      state.deletedSuccess = true;
    },

    //
    selectedCategoriaSuccess(state, action) {
      state.selectedCategoria = action.payload;
    },

    //
    selectedCategoriaById(state, action) {
      state.category = state.categories.find(
        (c) => Number(c.id) === Number(action.payload)
      );
    },

    getCategoriesOrder(state, action) {
      state.order = state.categories.map((p) => p.order);
    },

    cleanSelectedCategoria(state, action) {
      state.selectedCategoria = -1;
      state.category = null;
    },

    // CHANGE PAGE
    changePageSuccess(state, action) {
      state.selectedPage = action.payload;
    },

    changeProductsPerPage(state, action) {
      state.productPerPage = action.payload;
    },

    handleFilterName(state, action) {
      state.filterName = action.payload;
    },

    addImagesSuccess(state, action) {
      state.images = [...state.images, action.payload];
    },
    resetImages(state, action) {
      state.images = [];
    },
    removeSpecificImage(state, action) {
      state.images = state.images.filter((s) => s.name !== action.payload);
    },

    getTagsSuccess(state, action) {
      state.isLoading = false;
      state.tags = action.payload;
    },

    getTagSuccess(state, action) {
      state.isLoading = false;
      state.tag = state.tags.find((t) => t.id === Number(action.payload));
    },

    createTagSuccess(state, action) {
      state.isLoading = false;
      state.tags = [...state.tags, action.payload];
      state.createdSuccess = true;
    },

    updateTagSuccess(state, action) {
      state.isLoading = false;
      state.tags = state.tags.map((tag) => {
        if (Number(tag.id) === Number(action.payload.id)) return action.payload;
        else return tag;
      });
      state.updatedSuccess = true;
    },

    deleteTagSuccess(state, action) {
      state.isLoading = false;
      state.tags = state.tags.map((tag) => {
        if (tag.id === action.payload) {
          return { ...tag, isActive: false };
        }

        return tag;
      });
    },

    activateTagSuccess(state, action) {
      state.isLoading = false;
      state.tags = state.tags.map((tag) => {
        if (tag.id === action.payload) {
          return { ...tag, isActive: true };
        }

        return tag;
      });
    },

    cleanSelectedTag(state, action) {
      state.tag = null;
    },

    changeTagOrderPropertySuccess(state, action) {
      state.isLoading = false;
      state.tagOrderProperty = action.payload;
    },

    changeTagOrderBySuccess(state, action) {
      state.isLoading = false;
      state.tagOrderBy = action.payload;
    },

    changeTagsPerPageSuccess(state, action) {
      state.tagsPerPage = action.payload;
    },

    changeTagsPageSuccess(state, action) {
      state.selectedTagPage = action.payload;
    },

    changeFilterNameTags(state, action) {
      state.filterNameTags = action.payload;
    },

    setTagsOrder(state, action) {
      state.tagsOrder = action.payload;
    },

    resetTagsOrder(state, action) {
      state.tagsOrder = [];
    },
    changeSeeAll(state, action) {
      state.seeAll = action.payload;
    },

    changeSelectedTagProduct(state, action) {
      state.selectedTagProduct = action.payload;
    },
    setTagModeActive(state, action) {
      state.tagModeActive = !!action.payload;
      if (!action.payload) state.selectedTagProduct = null;
    },
    // Al cambiar de mesa, apagamos el modo y reseteamos el curso
    // seleccionado. Se dispara desde el listener del hub mesas.
    resetTagMode(state) {
      state.tagModeActive = false;
      state.selectedTagProduct = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCategoriesSuccess,
  getCategorySuccess,
  updateCategorySuccess,
  updateCategoryVisibilitySuccess,
  deleteCategorySuccess,
  selectedCategoriaSuccess,
  selectedCategoriaById,
  getCategoriesOrder,
  cleanSelectedCategoria,
  changePageSuccess,
  changeProductsPerPage,
  handleFilterName,
  setProductsOrder,
  resetProductsOrder,
  setCategoriesOrder,
  resetCategoriesOrder,
  addImagesSuccess,
  resetImages,
  removeSpecificImage,

  cleanSelectedTag,
  getTagsSuccess,
  getTagSuccess,
  createTagSuccess,
  updateTagSuccess,
  deleteTagSuccess,
  activateTagSuccess,
  changeTagOrderPropertySuccess,
  changeTagOrderBySuccess,
  changeTagsPageSuccess,
  changeTagsPerPageSuccess,
  changeFilterNameTags,
  setTagsOrder,
  resetTagsOrder,
  changeSeeAll,
  changeSelectedTagProduct,
  setTagModeActive,
  resetTagMode,
} = slice.actions;

// ----------------------------------------------------------------------

export function setCategoriaSelected(id) {
  return async (dispatch) => {
    dispatch(slice.actions.selectedCategoriaSuccess(id));
  };
}

export function getCategories(getAll, { force = false } = {}) {
  return async (dispatch) => {
    if (!getAll) getAll = false;
    // Ya viene en el bootstrap. Los llamadores locales (dispatch en
    // useEffect al entrar a producto/carta) reciben cache hit. Con el
    // ETag el segundo GET habría sido 304 sin body, pero staleGuard
    // ahorra incluso el round-trip → siempre gana.
    if (!force && staleGuard(`categorias/${getAll}`)) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`categoria/Get/${getAll}`);
      dispatch(slice.actions.getCategoriesSuccess(response.data.categorias));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCategoriesWithProducts(getAll) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    if (!getAll) getAll = false;

    try {
      const response = await axios.get(`categoria/GetWithOrder/${getAll}`);
      dispatch(slice.actions.getCategoriesSuccess(response.data.categorias));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getImages() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`categoria/getImages`);
      dispatch(slice.actions.getImagesSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCategory(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`categoria/GetById/${id}`);
      dispatch(slice.actions.getCategoriesSuccess(response.data.categoria));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function AddCategory(category) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post("categoria", JSON.stringify(category), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      const created = response.data?.categoria;
      dispatch(slice.actions.getCategorySuccess(created));
      // Retornamos la categoría creada para que callers como "Crear inline
      // desde el form de producto" puedan auto-seleccionarla. Propagamos
      // errores para que el caller pueda mostrar feedback (snackbar de error).
      return created;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function UpdateCategory(category) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updateCategorySuccess(category));

      await axios.put(`categoria`, JSON.stringify(category), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function UpdateCategoryVisibility(category) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updateCategoryVisibilitySuccess(category));

      await axios.put(`categoria/updateVisibility`, JSON.stringify(category), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteCategory(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.deleteCategorySuccess(id));
      const response = await axios.delete(`categoria/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateCategory(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`categoria/activate/${id}`);
      dispatch(slice.actions.activateCategorySuccess(id));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteSubCategory(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.delete(`categoria/subCategory/${id}`);
      dispatch(slice.actions.deleteSubCategorySuccess(id));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function handleVisibility(category) {
  const cat = {
    id: category.id,
    visible: !category.visible,
    visibleToClients:
      !category.visible === false ? false : category.visibleToClients,
  };
  return UpdateCategoryVisibility(cat);
}

export function handleClientsVisibility(category) {
  const cat = {
    id: category.id,
    visible: category.visible,
    visibleToClients: !category.visibleToClients,
  };
  return UpdateCategoryVisibility(cat);
}

//--------------------------------------------------------------------

export function getTags(getAll, { force = false } = {}) {
  return async (dispatch) => {
    // Los tags ya vienen hidratados en `loadTpvBootstrap()` (bloque `tags`).
    // Sin este guard, cada remount de ProductList/HubProductoSession/etc.
    // dispara un GET al backend que resuelve con 304 pero sigue haciendo
    // round-trip. Con TTL 5 min los remounts naturales del navegador
    // (cambio de ruta, tab background→foreground) no repiten el fetch.
    // Las mutations invalidan la cache del bloque `tags` en el backend, así
    // que los cambios se ven al siguiente ciclo de bootstrap.
    //
    // `force: true` para eventos SignalR — el guardián existe para
    // colapsar remounts naturales, no para bloquear avisos explícitos.
    if (!force && staleGuard(`categoria/tags/${getAll}`)) return;

    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`categoria/GetCourseLabels/${getAll}`);
      dispatch(slice.actions.getTagsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createTag(tag) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(
        `categoria/AddCourseLabel`,
        JSON.stringify(tag),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.createTagSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateTag(tag) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(
        `categoria/UpdateCourseLabel`,
        JSON.stringify(tag),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.updateTagSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteTag(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.deleteTagSuccess(id));
      await axios.delete(`categoria/DeleteCourseLabel/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateTag(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.activateTagSuccess(id));
      await axios.put(`categoria/ActivateCourseLabel/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
